import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { upperCase } from 'lodash'
import { Button, LinkButton } from '@/atoms/Button'
import { CaretDownIcon } from '@/atoms/Icons/CaretDownIcon'
import { GlobeIcon } from '@/atoms/Icons/GlobeIcon'
import { LogoutIcon } from '@/atoms/Icons/LogoutIcon'
import { NewsIcon } from '@/atoms/Icons/NewsIcon'
import { PeopleIcon } from '@/atoms/Icons/PeopleIcon'
import { PersonIcon } from '@/atoms/Icons/PersonIcon'
import { CaptionSM, ParagraphMD, ParagraphSM, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants'
import { Avatar } from '@/molecules/Avatar'
import { useUser } from '@/services/UserService'
import { GuildProject } from '@/types/codegen-federation'
import { useLoginUrl, useLogoutUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useFilmmakerDashboard } from '@/views/GuildCreateProjectsView/dashboard/context/FilmmakerDashboardContext'

export const FilmmakerMenu = () => {
  const router = useSafeRouter()
  const { isLoggedIn, user } = useUser()
  const { projects, selectedProject, admin } = useFilmmakerDashboard()
  const [defaultProject, setDefaultProject] = useState<GuildProject>()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const { logoutUrl, trackLogoutStarted } = useLogoutUrl()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { t } = useTranslate('guild-create')
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (selectedProject) {
      setDefaultProject(selectedProject)
      return
    }
    if (projects) {
      setDefaultProject(projects?.[0])
    }
  }, [projects, selectedProject])

  const associationType = useMemo(() => {
    const defAssoc = defaultProject?.projectAssociations?.find(
      (a) => a?.userId === user?.uuid && a?.status === 'COMPLETE',
    )
    return defAssoc?.associationType
  }, [defaultProject, user])

  const handleItemClick: MouseEventHandler = () => {
    setIsMenuOpen((prev) => !prev)
  }

  const handleDefaultSelection = useCallback(
    (id: string) => {
      if (defaultProject?.id === id) {
        setDefaultProject(undefined)
        return
      }
      setDefaultProject(projects?.find((p) => p.id === id))
    },
    [projects, setDefaultProject, defaultProject],
  )

  if (!isLoggedIn) {
    return (
      <div className="flex items-center">
        <LinkButton
          href={loginUrl}
          variant="plain"
          outline
          className="box-border h-8 rounded-md backdrop-blur-sm"
          onClick={() => trackLoginStarted('top-nav')}
        >
          <CaptionSM className="whitespace-nowrap">
            <Translate t={t} i18nKey="logIn">
              Log In
            </Translate>
          </CaptionSM>
        </LinkButton>
      </div>
    )
  }

  return (
    <div className="relative">
      <div>
        <Avatar onClick={handleItemClick} />
      </div>
      {isMenuOpen && (
        <div ref={ref} className="absolute right-0 top-0 z-50 mt-[40px] min-w-[390px] rounded-lg bg-black">
          <div className="flex items-center justify-between border-b border-b-gray-900 p-4">
            <div className="flex items-center gap-2">
              <Avatar size={48} />
              <div className="flex flex-col gap-0">
                <TitleMD weight="semibold" color="white">
                  {user?.profile?.firstName} {user?.profile?.lastName?.charAt(0)}.
                </TitleMD>
                <ParagraphSM color="gray-500" className="-mt-1">
                  {user?.email}
                </ParagraphSM>
              </div>
            </div>
            {associationType && (
              <ParagraphMD weight="semibold" className="text-sm" color="gray-400">
                {upperCase(associationType)}
              </ParagraphMD>
            )}
          </div>
          <Button
            variant="plain"
            className="flex items-center gap-2 px-8 pt-4"
            onClick={() => router.push(paths.account.index)}
          >
            <PersonIcon size={24} color={'gray-500'} />
            <ParagraphMD color="white">Profile</ParagraphMD>
          </Button>
          {projects && projects.length > 1 && (
            <div className="flex flex-col gap-4 p-4">
              {projects.map((p, index) => {
                const isSelected = p.id === defaultProject?.id
                return (
                  <>
                    <Button
                      key={`proj_${p.id}_${index}`}
                      variant="plain"
                      className="flex items-center justify-between"
                      onClick={() => handleDefaultSelection(p?.id as string)}
                    >
                      <ParagraphMD color="white">{p?.name}</ParagraphMD>
                      <motion.div initial={{ rotate: 0 }} animate={isSelected ? { rotate: 180 } : { rotate: 0 }}>
                        <CaretDownIcon size={16} color="white" />
                      </motion.div>
                    </Button>
                    {isSelected && (
                      <div className="flex flex-col gap-3 px-4">
                        <Button
                          variant="plain"
                          className="flex items-center gap-2"
                          onClick={(e) => {
                            router.push(`/guild/create/projects/${defaultProject?.id}?c=org`)
                            handleItemClick(e)
                          }}
                        >
                          <GlobeIcon size={24} color={'gray-500'} />
                          <ParagraphMD color="white">Organization</ParagraphMD>
                        </Button>
                        <Button
                          variant="plain"
                          className="flex items-center gap-2"
                          onClick={(e) => {
                            router.push(`/guild/create/projects/${defaultProject?.id}?c=stub`)
                            handleItemClick(e)
                          }}
                        >
                          <NewsIcon size={24} color={'gray-500'} />
                          <ParagraphMD color="white">Receipts</ParagraphMD>
                        </Button>
                        <Button
                          variant="plain"
                          className="flex items-center gap-2"
                          onClick={(e) => {
                            router.push(`/guild/create/projects/${defaultProject?.id}?c=team`)
                            handleItemClick(e)
                          }}
                        >
                          <PeopleIcon size={24} color={'gray-500'} />
                          <ParagraphMD color="white">Invite Team</ParagraphMD>
                        </Button>
                      </div>
                    )}
                  </>
                )
              })}
            </div>
          )}
          <div className="flex flex-col gap-3 px-8 pb-8">
            {projects?.length === 1 && defaultProject && (
              <>
                <Button
                  variant="plain"
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    router.push(`/guild/create/projects/${defaultProject?.id}?c=org`)
                    handleItemClick(e)
                  }}
                >
                  <GlobeIcon size={24} color={'gray-500'} />
                  <ParagraphMD color="white">Organization</ParagraphMD>
                </Button>
                <Button
                  variant="plain"
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    router.push(`/guild/create/projects/${defaultProject?.id}?c=stub`)
                    handleItemClick(e)
                  }}
                >
                  <NewsIcon size={24} color={'gray-500'} />
                  <ParagraphMD color="white">Receipts</ParagraphMD>
                </Button>
                <Button
                  variant="plain"
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    router.push(`/guild/create/projects/${defaultProject?.id}?c=team`)
                    handleItemClick(e)
                  }}
                >
                  <PeopleIcon size={24} color={'gray-500'} />
                  <ParagraphMD color="white">Invite Team</ParagraphMD>
                </Button>
              </>
            )}
            {admin && (
              <div className="flex items-center gap-2">
                <PersonIcon size={24} color={'gray-500'} />
                <ParagraphMD color="white">Employee</ParagraphMD>
              </div>
            )}
            <Button
              variant="plain"
              className="mt-6 flex items-center gap-2"
              onClick={() => {
                trackLogoutStarted('filmmaker-nav')
                router.push(logoutUrl)
              }}
            >
              <LogoutIcon size={24} color={'gray-500'} />
              <ParagraphMD color="white">Logout</ParagraphMD>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
